// src/components/Sidebar.jsx

import React from 'react';
import logo from '../assets/logo.webp';
function Sidebar({
  activeModule,
  setActiveModule,
  activeTab,
  setActiveTab,
  isOpen,
  setIsOpen,
  handleLogout
}) {
  const [showModuleDropdown, setShowModuleDropdown] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const dropdownRef = React.useRef(null);

  React.useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      // Don't auto-close on resize
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowModuleDropdown(false);
      }
      // Close sidebar on mobile when clicking outside the sidebar
      if (isMobile && isOpen) {
        const sidebar = document.querySelector('.sidebar-container');
        const toggleBtn = document.querySelector('.sidebar-toggle-btn');
        if (sidebar && !sidebar.contains(e.target) && toggleBtn && !toggleBtn.contains(e.target)) {
          setIsOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile, isOpen, setIsOpen]);

  const modules = [
    { id: 'nursery', label: 'Nursery Management', icon: '🌱', color: '#1b4d3e' },
    { id: 'marketing', label: 'Marketing Dashboard', icon: '📢', color: '#c9a03d' }
  ];

  const menuItems = [
    {
      id: 'inventory',
      label: 'Inventory',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2L2 12h3v8h14v-8h3L12 2z" />
          <path d="M12 12v8" />
        </svg>
      ),
      iconClosed: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2L2 12h3v8h14v-8h3L12 2z" />
        </svg>
      )
    },
    {
      id: 'pos-billing',
      label: 'POS Billing',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <circle cx="12" cy="12" r="2" />
          <path d="M8 12h0M16 12h0" />
        </svg>
      ),
      iconClosed: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <circle cx="12" cy="12" r="2" />
        </svg>
      )
    },
    {
      id: 'transactions',
      label: 'Transactions',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
          <line x1="8" y1="14" x2="16" y2="14" />
          <line x1="8" y1="18" x2="12" y2="18" />
        </svg>
      ),
      iconClosed: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      )
    },
    {
      id: 'advertisement-manager',
      label: 'Marketing Content',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M11 5L6 9H2v6h4l5 4V5z" />
          <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
          <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
        </svg>
      ),
      iconClosed: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M11 5L6 9H2v6h4l5 4V5z" />
        </svg>
      )
    },
  ];

  const currentMenuItems = activeModule === 'nursery'
    ? menuItems.filter(item => item.id !== 'advertisement-manager')
    : menuItems.filter(item => item.id === 'advertisement-manager');

  // Mobile overlay when sidebar is open on mobile
  const MobileOverlay = () => (
    isMobile && isOpen && (
      <div
        onClick={() => setIsOpen(false)}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 998,
          transition: 'opacity 0.3s ease',
          animation: 'fadeIn 0.3s ease'
        }}
      />
    )
  );

  return (
    <>
      <MobileOverlay />
      
      {/* Mobile Toggle Button - Always visible on mobile */}
      {isMobile && (
        <button
          onClick={() => setIsOpen(!isOpen)}
          style={{
            position: 'fixed',
            top: '12px',
            left: '12px',
            zIndex: 1001,
            width: '44px',
            height: '44px',
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
            background: '#ffffff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
            transition: 'all 0.3s ease'
          }}
          className="mobile-toggle-btn"
          aria-label="Toggle Sidebar"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
      )}

      <div style={sidebarStyle(isOpen, isMobile)} className="sidebar-container">
        <style>{`
          ${animationStyles}
          ${mobileStyles}
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideIn {
            from { transform: translateX(-100%); }
            to { transform: translateX(0); }
          }
          @keyframes slideOut {
            from { transform: translateX(0); }
            to { transform: translateX(-100%); }
          }
        `}</style>
        
        <div style={logoContainerStyle(isOpen, isMobile)}>
          {/* Mobile Close Button - Inside sidebar when open */}
          {isMobile && isOpen && (
            <button
              onClick={() => setIsOpen(false)}
              style={mobileCloseButtonStyle}
              aria-label="Close sidebar"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}

          {/* Desktop Toggle Button - Hidden on mobile */}
          {!isMobile && (
            <button
              onClick={() => setIsOpen(!isOpen)}
              style={toggleButtonStyle(isOpen, isMobile)}
              title={isOpen ? "Close Sidebar" : "Open Sidebar"}
              className="sidebar-toggle-btn"
            >
              {isOpen ? (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <line x1="2" y1="4" x2="14" y2="4" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                  <line x1="2" y1="8" x2="14" y2="8" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                  <line x1="2" y1="12" x2="14" y2="12" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <line x1="3" y1="4.5" x2="15" y2="4.5" stroke="#475569" strokeWidth="2" strokeLinecap="round" />
                  <line x1="3" y1="9" x2="15" y2="9" stroke="#475569" strokeWidth="2" strokeLinecap="round" />
                  <line x1="3" y1="13.5" x2="15" y2="13.5" stroke="#475569" strokeWidth="2" strokeLinecap="round" />
                </svg>
              )}
            </button>
          )}

          <div style={logoWrapper(isOpen)}>
            <div style={logoImageWrapper(isOpen)}>
              <div style={logoGlowRing(isOpen)}></div>
              <img
                src={logo}
                alt="Nursery Logo"
                style={logoImageStyle(isOpen, isMobile)}
                onError={(e) => (e.target.style.display = 'none')}
              />
              {isOpen && <div style={logoBadge}>✨</div>}
            </div>
            {isOpen && (
              <div style={logoTextWrapper}>
                <div style={logoTopLine}></div>
                <h2 style={logoMainTextStyle}>
                  <span style={logoMainFirstWord}>NASMAT</span>
                  <span style={logoMainSecondWord}>AL ZOHOUR</span>
                </h2>
                <div style={logoDivider}>
                  <span style={logoDividerDot}>✿</span>
                </div>
                <p style={logoSubTextStyle}>PREMIUM NURSERY & GARDEN CENTER</p>
                <div style={logoBottomLine}></div>
              </div>
            )}
          </div>
        </div>

        {/* Module Switcher */}
        {isOpen && (
          <div style={{ padding: isMobile ? '0 12px 16px 12px' : '0 16px 20px 16px' }} ref={dropdownRef}>
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowModuleDropdown(!showModuleDropdown)}
                style={moduleSwitcherButtonStyle(activeModule, isMobile)}
                className="module-switcher-btn"
              >
                <div style={moduleIndicator(activeModule)}>
                  {modules.find(m => m.id === activeModule)?.icon}
                </div>
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <div style={moduleLabelSmall}>ACTIVE MODULE</div>
                  <div style={moduleLabelMain(isMobile)}>
                    {modules.find(m => m.id === activeModule)?.label}
                  </div>
                </div>
                <span style={{ fontSize: '10px', opacity: 0.5 }}>{showModuleDropdown ? '▲' : '▼'}</span>
              </button>

              {showModuleDropdown && (
                <div style={moduleDropdownStyle(isMobile)}>
                  {modules.map(mod => (
                    <button
                      key={mod.id}
                      onClick={() => {
                        setActiveModule(mod.id);
                        setShowModuleDropdown(false);
                        if (mod.id === 'nursery') setActiveTab('inventory');
                        else setActiveTab('advertisement-manager');
                        if (isMobile) setIsOpen(false);
                      }}
                      style={moduleOptionStyle(activeModule === mod.id, isMobile)}
                    >
                      <span style={{ fontSize: isMobile ? '18px' : '16px' }}>{mod.icon}</span>
                      <span style={{ fontWeight: activeModule === mod.id ? '700' : '500', fontSize: isMobile ? '13px' : '12px' }}>
                        {mod.label}
                      </span>
                      {activeModule === mod.id && <span style={{ marginLeft: 'auto', color: '#c9a03d' }}>✓</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {!isOpen && !isMobile && (
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <button
              onClick={() => setIsOpen(true)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '20px',
                padding: '10px',
                borderRadius: '10px',
                transition: 'background 0.2s',
                width: '100%'
              }}
              title="Switch Module"
              className="module-icon-btn"
            >
              {modules.find(m => m.id === activeModule)?.icon}
            </button>
          </div>
        )}

        <nav style={navStyle(isOpen, isMobile)} className="sidebar-nav">
          {currentMenuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                setActiveTab(item.id);
                if (isMobile) setIsOpen(false);
              }}
              style={{
                ...menuItemStyle(isOpen, isMobile),
                justifyContent: isOpen ? 'flex-start' : 'center',
                background: activeTab === item.id ? (isMobile ? '#475569' : '#475569') : 'transparent',
                color: activeTab === item.id ? 'white' : '#374151',
                borderLeft: activeTab === item.id && isOpen ? '3px solid #c9a03d' : '3px solid transparent',
                borderRight: activeTab === item.id && !isOpen ? '3px solid #c9a03d' : '3px solid transparent',
                padding: isOpen ? (isMobile ? '12px 14px' : '10px 12px') : (isMobile ? '14px' : '10px'),
                borderRadius: isMobile ? '10px' : '12px',
                marginBottom: isMobile ? '4px' : '6px'
              }}
              title={!isOpen ? item.label : ''}
              className="sidebar-menu-item"
            >
              <span style={menuIconStyle(isOpen)}>
                {isOpen ? item.icon : item.iconClosed}
              </span>
              {isOpen && (
                <span style={menuLabelStyle(isMobile)}>
                  {item.label}
                </span>
              )}
              {isOpen && activeTab === item.id && <span style={activeIndicator}>●</span>}
            </button>
          ))}
        </nav>

        {isOpen ? (
          <div style={footerStyle(isMobile)}>
            <button
              onClick={handleLogout}
              style={logoutButtonStyle(isMobile)}
              className="logout-btn"
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #fff0e0, #ffe8d4)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(201, 160, 61, 0.2)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #fff9f0, #fff5e6)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              🚪 {isMobile ? 'LOGOUT' : 'LOGOUT'}
            </button>

            <div style={footerLine}></div>
            <p style={footerText(isMobile)}>© 2024 NASMAT AL ZOHOUR</p>
            <p style={footerVersion(isMobile)}>Premium Nursery Collection</p>
          </div>
        ) : (
          !isMobile && (
            <div style={footerMiniStyle(isMobile)}>
              <div style={footerMiniIcon}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#c9a03d" strokeWidth="1.5">
                  <path d="M12 2L2 12h3v8h14v-8h3L12 2z" />
                  <circle cx="12" cy="16" r="2" fill="#c9a03d" stroke="none" />
                </svg>
              </div>
            </div>
          )
        )}
      </div>
    </>
  );
}

/* =========================
   STYLES - PREMIUM DESIGN
========================= */

const baseFontFamily = "'Book Antiqua', 'Baskerville Old Face', 'Times New Roman', Georgia, serif";

const sidebarStyle = (isOpen, isMobile) => ({
  width: isOpen ? (isMobile ? '280px' : '280px') : (isMobile ? '0px' : '72px'),
  background: 'linear-gradient(180deg, #ffffff 0%, #faf9f7 100%)',
  borderRight: isMobile && !isOpen ? 'none' : '1px solid #e8e2d8',
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  position: isMobile ? 'fixed' : 'sticky',
  top: 0,
  left: 0,
  flexShrink: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  boxShadow: isMobile 
    ? (isOpen ? '4px 0 30px rgba(0,0,0,0.15)' : 'none')
    : '2px 0 20px rgba(0,0,0,0.08)',
  transition: isMobile 
    ? 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    : 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  fontFamily: baseFontFamily,
  zIndex: isMobile ? 999 : 'auto',
  transform: isMobile && isOpen ? 'translateX(0)' : (isMobile && !isOpen ? 'translateX(-100%)' : 'none'),
});

const toggleButtonStyle = (isOpen, isMobile) => ({
  position: 'absolute',
  top: isOpen ? '16px' : '20px',
  right: isOpen ? '16px' : 'auto',
  left: isOpen ? 'auto' : '50%',
  transform: isOpen ? 'none' : 'translateX(-50%)',
  width: '32px',
  height: '32px',
  borderRadius: '10px',
  border: '1px solid #e8e2d8',
  background: isOpen ? '#475569' : '#ffffff',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  zIndex: 10,
  transition: 'all 0.3s ease',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  fontFamily: baseFontFamily,
  color: isOpen ? '#fff' : '#475569'
});

const logoContainerStyle = (isOpen, isMobile) => ({
  position: 'relative',
  padding: isOpen ? (isMobile ? '20px 16px 20px 16px' : '28px 20px 24px 20px') : (isMobile ? '20px 8px' : '60px 12px 20px 12px'),
  borderBottom: isMobile && !isOpen ? 'none' : '1px solid #e8e2d8',
  marginBottom: isMobile ? '12px' : '20px',
  background: isMobile 
    ? 'linear-gradient(135deg, #2c4a3e, #1b4d3e)' 
    : 'linear-gradient(135deg, #faf9f7 0%, #ffffff 100%)',
  display: 'flex',
  justifyContent: 'center',
  borderRadius: isMobile && isOpen ? '0 0 20px 20px' : '0'
});

const mobileCloseButtonStyle = {
  position: 'absolute',
  top: '12px',
  right: '12px',
  background: 'rgba(255,255,255,0.2)',
  border: '2px solid rgba(255,255,255,0.3)',
  borderRadius: '10px',
  width: '40px',
  height: '40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: '#fff',
  zIndex: 10,
  transition: 'background 0.2s',
  backdropFilter: 'blur(8px)'
};

const logoWrapper = (isOpen) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: isOpen ? '16px' : '8px',
  width: '100%'
});

const logoImageWrapper = (isOpen) => ({
  position: 'relative',
  display: 'inline-flex',
  justifyContent: 'center'
});

const logoGlowRing = (isOpen) => ({
  position: 'absolute',
  top: '-4px',
  left: '-4px',
  right: '-4px',
  bottom: '-4px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #c9a03d, #f4e4bc, #c9a03d)',
  opacity: isOpen ? 0.6 : 0.8,
  zIndex: 0,
  animation: 'pulse 2s ease-in-out infinite'
});

const logoImageStyle = (isOpen, isMobile) => ({
  width: isOpen ? (isMobile ? '56px' : '70px') : (isMobile ? '40px' : '44px'),
  height: isOpen ? (isMobile ? '56px' : '70px') : (isMobile ? '40px' : '44px'),
  objectFit: 'cover',
  borderRadius: '50%',
  boxShadow: isMobile 
    ? '0 4px 16px rgba(0,0,0,0.3)'
    : '0 8px 20px rgba(201, 160, 61, 0.25), 0 2px 4px rgba(0,0,0,0.05)',
  border: isMobile ? '3px solid rgba(255,255,255,0.8)' : '3px solid #ffffff',
  transition: 'all 0.3s ease',
  display: 'block',
  position: 'relative',
  zIndex: 1
});

const logoBadge = {
  position: 'absolute',
  bottom: '-5px',
  right: '-8px',
  background: 'linear-gradient(135deg, #c9a03d, #b8860b)',
  borderRadius: '50%',
  width: '22px',
  height: '22px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '12px',
  boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
  zIndex: 2,
  border: '2px solid #ffffff'
};

const logoTextWrapper = {
  textAlign: 'center',
  width: '100%'
};

const logoTopLine = {
  width: '40px',
  height: '2px',
  background: 'linear-gradient(90deg, transparent, #c9a03d, transparent)',
  margin: '0 auto 12px auto'
};

const logoMainTextStyle = {
  margin: 0,
  fontSize: '14px',
  fontWeight: '700',
  letterSpacing: '1px',
  lineHeight: '1.4',
  fontFamily: "'Book Antiqua', 'Times New Roman', serif",
  display: 'flex',
  flexDirection: 'column',
  gap: '2px'
};

const logoMainFirstWord = {
  color: '#2c4a3e',
  display: 'block',
  fontSize: '15px',
  fontWeight: '800'
};

const logoMainSecondWord = {
  color: '#c9a03d',
  display: 'block',
  fontSize: '13px',
  fontWeight: '700',
  letterSpacing: '1.5px'
};

const logoDivider = {
  width: '100%',
  textAlign: 'center',
  margin: '10px 0',
  position: 'relative'
};

const logoDividerDot = {
  color: '#c9a03d',
  fontSize: '14px',
  background: '#ffffff',
  padding: '0 8px',
  position: 'relative',
  zIndex: 1
};

const logoSubTextStyle = {
  margin: '8px 0 0 0',
  fontSize: '8px',
  fontWeight: '600',
  color: '#8b7355',
  letterSpacing: '1px',
  fontFamily: "'Book Antiqua', 'Times New Roman', serif",
  textTransform: 'uppercase'
};

const logoBottomLine = {
  width: '60px',
  height: '1px',
  background: 'linear-gradient(90deg, #c9a03d, #f4e4bc, #c9a03d)',
  margin: '10px auto 0 auto'
};

const navStyle = (isOpen, isMobile) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: isMobile ? '4px' : '6px',
  padding: isMobile ? '0 12px' : '0 12px',
  flex: 1,
  marginTop: isMobile ? '8px' : '0'
});

const menuItemStyle = (isOpen, isMobile) => ({
  display: 'flex',
  alignItems: 'center',
  gap: isMobile ? '14px' : '12px',
  border: 'none',
  borderRadius: isMobile ? '10px' : '12px',
  cursor: 'pointer',
  fontSize: isMobile ? '14px' : '13px',
  fontWeight: isMobile ? '600' : '500',
  width: '100%',
  textAlign: 'left',
  transition: 'all 0.2s ease',
  position: 'relative',
  fontFamily: baseFontFamily,
  minHeight: isMobile ? '48px' : 'auto',
  padding: isOpen ? (isMobile ? '12px 14px' : '10px 12px') : (isMobile ? '14px' : '10px')
});

const menuIconStyle = (isOpen) => ({
  minWidth: isOpen ? '32px' : 'auto',
  textAlign: 'center',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: isOpen ? '32px' : 'auto'
});

const menuLabelStyle = (isMobile) => ({
  flex: 1,
  whiteSpace: 'nowrap',
  fontFamily: baseFontFamily,
  letterSpacing: '0.3px',
  fontSize: isMobile ? '13px' : '12px',
  fontWeight: isMobile ? '600' : '500'
});

const activeIndicator = {
  fontSize: '8px',
  color: '#c9a03d',
  marginLeft: 'auto'
};

const footerStyle = (isMobile) => ({
  padding: isMobile ? '16px 16px' : '20px 16px',
  borderTop: isMobile ? '1px solid rgba(0,0,0,0.06)' : '1px solid #e8e2d8',
  marginTop: 'auto',
  textAlign: 'center'
});

const logoutButtonStyle = (isMobile) => ({
  width: '100%',
  padding: isMobile ? '12px' : '10px',
  marginBottom: isMobile ? '12px' : '16px',
  borderRadius: '12px',
  border: '1px solid rgba(201, 160, 61, 0.3)',
  background: 'linear-gradient(135deg, #fff9f0, #fff5e6)',
  color: '#8b6914',
  cursor: 'pointer',
  fontWeight: isMobile ? '700' : '600',
  letterSpacing: '0.5px',
  transition: 'all 0.3s ease',
  fontFamily: "'Book Antiqua', 'Times New Roman', serif",
  fontSize: isMobile ? '13px' : '12px',
  minHeight: isMobile ? '48px' : 'auto'
});

const footerLine = {
  width: '50px',
  height: '1px',
  background: 'linear-gradient(90deg, transparent, #c9a03d, transparent)',
  margin: '0 auto 12px auto',
  borderRadius: '2px'
};

const footerText = (isMobile) => ({
  margin: '0 0 4px 0',
  fontSize: isMobile ? '9px' : '8px',
  color: '#a89f91',
  fontWeight: '500',
  fontFamily: baseFontFamily,
  letterSpacing: '0.5px'
});

const footerVersion = (isMobile) => ({
  margin: 0,
  fontSize: isMobile ? '8px' : '7px',
  color: '#c9a03d',
  fontFamily: baseFontFamily,
  letterSpacing: '0.3px'
});

const footerMiniStyle = (isMobile) => ({
  padding: isMobile ? '20px 0' : '16px 0',
  borderTop: isMobile ? '1px solid rgba(0,0,0,0.06)' : '1px solid #e8e2d8',
  marginTop: 'auto',
  textAlign: 'center'
});

const footerMiniIcon = {
  fontSize: '18px',
  opacity: 0.6,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const animationStyles = `
  @keyframes pulse {
    0%, 100% {
      opacity: 0.4;
    }
    50% {
      opacity: 0.8;
    }
  }
`;

const moduleSwitcherButtonStyle = (activeModule, isMobile) => ({
  width: '100%',
  padding: isMobile ? '12px 12px' : '10px 12px',
  background: isMobile ? 'rgba(255,255,255,0.9)' : '#ffffff',
  border: isMobile ? '2px solid rgba(255,255,255,0.2)' : '1px solid #e8e2d8',
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  gap: isMobile ? '12px' : '10px',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  boxShadow: isMobile ? '0 4px 12px rgba(0,0,0,0.08)' : '0 2px 8px rgba(0,0,0,0.04)',
  outline: 'none',
  fontFamily: baseFontFamily,
  minHeight: isMobile ? '52px' : 'auto',
  backdropFilter: isMobile ? 'blur(8px)' : 'none'
});

const moduleIndicator = (activeModule) => ({
  width: '32px',
  height: '32px',
  borderRadius: '8px',
  background: activeModule === 'nursery' ? '#f0fdf4' : '#fff9f0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '18px',
  boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)'
});

const moduleLabelSmall = {
  fontSize: '8px',
  fontWeight: '700',
  color: '#a89f91',
  letterSpacing: '0.5px',
  marginBottom: '1px'
};

const moduleLabelMain = (isMobile) => ({
  fontSize: isMobile ? '12px' : '11px',
  fontWeight: '700',
  color: '#2c4a3e',
  letterSpacing: '0.2px'
});

const moduleDropdownStyle = (isMobile) => ({
  position: 'absolute',
  top: 'calc(100% + 8px)',
  left: 0,
  right: 0,
  background: isMobile ? 'rgba(255,255,255,0.98)' : 'white',
  backdropFilter: isMobile ? 'blur(12px)' : 'none',
  borderRadius: '12px',
  boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.01)',
  zIndex: 100,
  border: '1px solid #e8e2d8',
  overflow: 'hidden',
  padding: '6px'
});

const moduleOptionStyle = (isActive, isMobile) => ({
  width: '100%',
  padding: isMobile ? '12px 14px' : '10px 12px',
  background: isActive ? (isMobile ? 'rgba(0,0,0,0.04)' : '#faf9f7') : 'transparent',
  border: 'none',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  cursor: 'pointer',
  fontSize: isMobile ? '13px' : '12px',
  color: isActive ? '#2c4a3e' : '#475569',
  textAlign: 'left',
  transition: 'all 0.2s ease',
  fontFamily: baseFontFamily,
  minHeight: isMobile ? '48px' : 'auto'
});

const mobileStyles = `
  /* Mobile Styles */
  @media screen and (max-width: 768px) {
    .sidebar-container {
      border-radius: 0;
    }
    
    /* Show sidebar when open */
    .sidebar-container[style*="translateX(0)"] {
      display: flex !important;
    }
    
    .sidebar-menu-item {
      font-size: 14px !important;
    }
    
    .sidebar-menu-item:active {
      transform: scale(0.96);
    }
    
    .module-switcher-btn:active {
      transform: scale(0.98);
    }
    
    .logout-btn:active {
      transform: scale(0.96);
    }
    
    .module-icon-btn:active {
      transform: scale(0.9);
    }
    
    .mobile-toggle-btn:active {
      transform: scale(0.92);
    }
    
    /* Hide scrollbar for mobile */
    .sidebar-container::-webkit-scrollbar {
      width: 0;
      background: transparent;
    }
    
    .sidebar-container {
      scrollbar-width: none;
      -ms-overflow-style: none;
    }
  }

  /* Touch-friendly improvements for mobile */
  @media (hover: none) and (pointer: coarse) {
    .sidebar-menu-item {
      min-height: 48px !important;
    }
    
    .sidebar-menu-item:active {
      background: rgba(71, 85, 105, 0.1) !important;
    }
    
    .module-switcher-btn {
      min-height: 52px !important;
    }
    
    .logout-btn {
      min-height: 48px !important;
    }
    
    .mobile-toggle-btn {
      min-height: 44px !important;
      min-width: 44px !important;
    }
  }

  /* Landscape phones */
  @media screen and (max-width: 768px) and (orientation: landscape) {
    .sidebar-container {
      height: 100vh !important;
    }
  }
`;

export default Sidebar;
